import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import get from 'lodash/get';

import loadScript from 'lib/loadScript';
import Breakpoints from 'lib/Breakpoints';
import placements from 'lib/taboolaConfig.json';
import MobileDetection from 'lib/MobileDetection';
import { setTaboolaLoaded } from 'redux/modules/shared';

import styles from './styles.module.scss';

const blogSubtype = 'blog';
const liveBlogSubtype = 'liveBlog';

const verticalWithTaboola = {
  news: true,
  today: true,
  msnbc: true,
  noticias: true,
  entretenimiento: true,
  shows: true,
  telemundo: true,
};

// exported for testing
export const mapStateToProps = ({
  amp, front, shared, article,
}) => ({
  isLiveBlog:
    article
    && article.content[0]
      && (article.content[0].subType === blogSubtype
        || article.content[0].subType === liveBlogSubtype),

  ampVertical: amp.vertical,
  vertical: shared.vertical,
  pageView: shared.pageView === 'card' ? 'article' : shared.pageView,
  section: shared.section,
  isChromeless: shared.isChromeless,
  loaderLoaded: shared.taboolaLoaderLoaded,
  adsDisabled: front.curation.adsDisabled,
  taboolaFeed: shared.taboolaFeed,
  ecommerceEnabled: article && article.content[0] && article.content[0].ecommerceEnabled,
});

class Taboola extends React.Component {
  static propTypes = {
    ampVertical: PropTypes.string,
    dispatch: PropTypes.func.isRequired,
    vertical: PropTypes.string.isRequired,
    id: PropTypes.string,
    pageView: PropTypes.string.isRequired,
    section: PropTypes.string,
    canonicalUrl: PropTypes.string,
    isRail: PropTypes.bool,
    isChromeless: PropTypes.bool.isRequired,
    isLiveBlog: PropTypes.bool,
    loaderLoaded: PropTypes.bool.isRequired,
    className: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    adsDisabled: PropTypes.bool,
    pkgClassName: PropTypes.string,
    ecommerceEnabled: PropTypes.bool,
  };

  static contextTypes = {
    amp: PropTypes.bool,
    isLiveBlog: PropTypes.bool,
    store: PropTypes.shape({}),
  };

  static defaultProps = {
    ampVertical: null,
    id: '1',
    section: null,
    canonicalUrl: null,
    isRail: false,
    className: null,
    adsDisabled: false,
    pkgClassName: 'pkg taboola',
    ecommerceEnabled: false,
    isLiveBlog: false,
  };

  publisherConfig = {
    default: 'nbcnews',
    msnbc: 'nbcuniversal-msnbc',
    news: 'nbcnews',
    noticias: 'telemundocom',
    entretenimiento: 'telemundocom',
    shows: 'telemundocom',
    telemundo: 'telemundocom',
    today: 'nbc-today',
  };

  publisherChromelessConfig = {
    today: {
      android: 'nbctodayappandroid',
      ios: 'nbctodayappios',
    },
    news: {
      android: 'nbcnewsappandroid',
      ios: 'nbcnewsappios',
    },
    mach: {
      android: 'nbcnewsappandroid',
      ios: 'nbcnewsappios',
    },
    better: {
      android: 'nbcnewsappandroid',
      ios: 'nbcnewsappios',
    },
    think: {
      android: 'nbcnewsappandroid',
      ios: 'nbcnewsappios',
    },
    noticias: {
      android: 'telemundocom-app-android',
      ios: 'telemundocom-app-ios',
    },
    entretenimiento: {
      android: 'telemundocom-app-android',
      ios: 'telemundocom-app-ios',
    },
    shows: {
      android: 'telemundocom-app-android',
      ios: 'telemundocom-app-ios',
    },
    telemundo: {
      android: 'telemundocom-app-android',
      ios: 'telemundocom-app-ios',
    },
    default: 'nbcnews-app',
  };


  async componentDidMount() {
    const {
      id, isChromeless, isRail, vertical,
    } = this.props;
    const { amp } = this.context;

    if (
      // IRL, this check for `amp` wouldn't be necessary, however because of tests relying
      // on using `mount` and setting up the full redux store, we need to return early
      amp
      || (MobileDetection.Android() && vertical === 'today' && isChromeless)
    ) return;

    const placement = this.getPlacement();

    if (placement) {
      const placementConfig = { target_type: 'mix', ...placement };
      placementConfig.container = placement.container.replace('%id', id);

      this.taboolaContainer.id = placementConfig.container;
      // Delay the article rail configuration until the article placement is added.
      if (isRail) {
        return;
      }
      /* eslint-disable no-underscore-dangle */
      window._taboola = window._taboola || [];
      // Push placement config
      window._taboola.push(placementConfig);

      this.addConfig();

      await this.getLoader();

      if (window.performance && typeof window.performance.mark === 'function') {
        window.performance.mark('tbl_ic');
      }
      /* eslint-enable no-underscore-dangle */
    }
  }

  shouldComponentUpdate() {
    return false;
  }

  getChromelessLoaderPublisherID = () => {
    const {
      vertical,
    } = this.props;

    const device = MobileDetection.Android() ? 'android' : 'ios';

    return this.publisherChromelessConfig[vertical]
      ? this.publisherChromelessConfig[vertical][device]
      : this.publisherChromelessConfig.default;
  };

  getLoader = () => {
    const {
      vertical, isChromeless, loaderLoaded, dispatch,
    } = this.props;
    if (!loaderLoaded) {
      // Publisher name
      const publisher = isChromeless
        ? this.getChromelessLoaderPublisherID()
        : this.publisherConfig[vertical] || this.publisherConfig.default;

      // Loader name
      // taboola feed only chromeless only works for news articles and if enabled as query
      // TODO: change this when feed is ready on chromeless today
      let loader = 'loader';
      if (this.shouldShowTaboolaFeedOnChromeless()) {
        loader = 'mobile-loader';
      }

      dispatch(setTaboolaLoaded());

      return loadScript(`//cdn.taboola.com/libtrc/${publisher}/${loader}.js`);
    }
    return null;
  };

  getPlacement = () => {
    const type = this.getTypeFromPageSection();
    const {
      ampVertical,
      ecommerceEnabled,
      isChromeless,
      isRail,
      isLiveBlog,
      vertical: propVertical,
    } = this.props;

    const { amp } = this.context;
    const vertical = amp ? ampVertical : propVertical;

    if (vertical === 'today' && MobileDetection.Android() && isChromeless) {
      return {};
    }

    let breakpoint = this.getBreakpoint();

    if (isRail) {
      breakpoint = Breakpoints.isLorXL() && !isChromeless ? 'rail' : null;
    }

    // ecommerce placement
    if (vertical === 'today' && ecommerceEnabled && !isChromeless && !isRail && !amp) {
      if (breakpoint === 'mobile') {
        return placements.today.article['commerce-mobile'];
      }
      return placements.today.article.commerce;
    }

    if (amp && ecommerceEnabled) {
      return placements.today.article['amp-commerce'];
    }

    const isArticle = type === 'article';
    if (isArticle && isLiveBlog) {
      return placements.news.liveBlog[breakpoint];
    }

    const specificPlacement = this.getSpecificPlacement(vertical, type, breakpoint);
    return specificPlacement;
  };

  getSpecificPlacement = (
    vertical,
    type,
    breakpoint,
  ) => get(placements, [vertical, type, breakpoint], null);

  getTypeFromPageSection = () => {
    const { vertical, pageView, section } = this.props;

    const isTodayCoverABTestPage = (section === 'today-cover-redesign' || section === 'cover');
    if (pageView === 'front' && verticalWithTaboola[vertical]) {
      if (section === '' || isTodayCoverABTestPage) {
        return 'home';
      }

      return 'category';
    }
    if (pageView === 'card') {
      return 'article';
    }
    return pageView;
  };

  getBreakpoint = () => {
    const { amp } = this.context;
    const { isChromeless } = this.props;
    if (amp) {
      return 'amp';
    }
    if (this.shouldShowTaboolaFeedOnChromeless()) {
      return Breakpoints.isS() ? 'chromeless_feed' : 'chromeless_tablet';
    }
    if (isChromeless) {
      return Breakpoints.isS() ? 'chromeless_mobile' : 'chromeless_tablet';
    }
    if (Breakpoints.isS()) {
      return 'mobile';
    }

    return 'desktop';
  };

  shouldShowTaboolaFeedOnChromeless = () => {
    const {
      isChromeless,
      vertical,
    } = this.props;

    if (vertical === 'today' && MobileDetection.iOS() && isChromeless) {
      return true;
    }

    return vertical === 'news' && isChromeless;
  };

  ampComponent = () => {
    const placement = this.getPlacement();
    return placement && (
      <amp-embed
        width="100"
        height="100"
        type="taboola"
        heights={placement.heights}
        data-publisher={placement.publisher}
        data-mode={placement.mode}
        data-placement={placement.placement}
        data-target_type="mix"
        data-article="auto"
        data-url=""
      />
    );
  };

  addConfig = () => {
    const {
      isChromeless, id, canonicalUrl, vertical, isLiveBlog,
    } = this.props;
    const type = this.getTypeFromPageSection();
    const pageConfig = type === 'recipe'
      ? { category: 'auto' }
      : { [type]: 'auto' };
    const isArticle = type === 'article';

    /* eslint-disable no-underscore-dangle */

    if (isArticle) {
      if (!isChromeless) {
        let railPlacement = this.getSpecificPlacement(vertical, type, 'rail');
        if (isLiveBlog) {
          railPlacement = this.getSpecificPlacement(vertical, 'liveBlog', 'rail');
        }

        if (railPlacement) {
          const railPlacementConfig = { target_type: 'mix', ...railPlacement };
          railPlacementConfig.container = railPlacementConfig.container.replace('%id', id);
          window._taboola.push(railPlacementConfig);
        }
      }

      if (canonicalUrl) {
        pageConfig.url = canonicalUrl;
      }
    }

    if (this.shouldShowTaboolaFeedOnChromeless()) {
      window._taboola.mobile = [];
      window._taboola.mobile.push({
        // run sdkless when testing the js on a browser (no sdk) (optional)
        allow_sdkless_load: true,
        taboola_view_id: new Date().getTime(),
        // override publisher, this value is injected by mobile
        publisher: this.getChromelessLoaderPublisherID(),
      });
    }

    window._taboola.push(pageConfig);

    if (type !== 'article') {
      window._taboola.push({ flush: true });
    }
  };

  render() {
    const {
      adsDisabled,
      id,
      pkgClassName,
      className: propsClassName,
    } = this.props;
    const { amp } = this.context;

    // Do NOT show Taboola ads if adsDisabled is true.
    if (adsDisabled) return null;

    if (amp) {
      return this.ampComponent();
    }

    // the same markup should be rendered on the server as well as on the initial
    // render cycle on the client
    return (
      <div
        data-packageid={id}
        className={classNames(pkgClassName, styles.taboola, propsClassName, 'dn-print')}
        data-test="taboola"
      >
        <div data-test="placeholder" ref={(taboola) => { this.taboolaContainer = taboola; }} />
      </div>
    );
  }
}

// unconnected component used for testing
export { Taboola as UnconnectedTaboola };

export default connect(mapStateToProps)(Taboola);
